import type { CategoryMeta, MediaItem, MediaType } from "@/types/media";

export const categories: CategoryMeta[] = [
  {
    type: "movie",
    slug: "movie",
    label: "日韩电影",
    enLabel: "Movies",
    description: "从院线新片到影展佳作，收录日韩两国剧情、悬疑、爱情与治愈系电影，按类型、地区与年份快速筛选。",
    icon: "ri-film-line",
  },
  {
    type: "tv",
    slug: "tv",
    label: "日韩电视剧",
    enLabel: "TV Series",
    description: "追踪当季日剧韩剧、口碑短剧与经典长篇，覆盖悬疑、爱情、古装、职场等题材，持续更新。",
    icon: "ri-tv-2-line",
  },
  {
    type: "variety",
    slug: "variety",
    label: "日韩综艺",
    enLabel: "Variety",
    description: "治愈系慢综艺、美食纪实、音乐竞技与旅行节目，收录日韩两国高评分综艺内容。",
    icon: "ri-mic-line",
  },
];

export const navLinks = [
  { to: "/", label: "首页" },
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/category/variety", label: "综艺" },
  { to: "/news", label: "资讯" },
  { to: "/search", label: "搜索" },
];

export const typeLabelMap: Record<MediaType, string> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
};

export function getCategoryMeta(slug?: string): CategoryMeta | undefined {
  return categories.find((item) => item.slug === slug);
}

export function getMediaById(items: MediaItem[], id?: string): MediaItem | undefined {
  return items.find((item) => item.id === id);
}

export function formatDateCn(date: string): string {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
}

export interface FilterOptions {
  type?: MediaType;
  genres?: string[];
  region?: string;
  year?: string;
  keyword?: string;
  sort?: "latest" | "rating" | "heat" | "update";
}

export function collectGenres(items: MediaItem[], type?: MediaType): string[] {
  const pool = type ? items.filter((item) => item.type === type) : items;
  const set = new Set<string>();
  pool.forEach((item) => item.genres.forEach((genre) => set.add(genre)));
  return Array.from(set);
}

export function collectYears(items: MediaItem[]): number[] {
  const set = new Set<number>();
  items.forEach((item) => set.add(item.year));
  return Array.from(set).sort((a, b) => b - a);
}

export function searchMedia(items: MediaItem[], keyword: string): MediaItem[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return items;

  const matchesKeyword = (item: MediaItem) => {
    const haystack = [
      item.title,
      item.originalTitle,
      item.director,
      item.genres.join(" "),
      item.cast.join(" "),
      item.region,
      typeLabelMap[item.type],
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  };

  const direct = items.filter(matchesKeyword);
  if (direct.length > 0) return direct;

  const genreMatches = items.filter((item) =>
    item.genres.some((genre) => key.includes(genre) || genre.includes(key)),
  );
  if (genreMatches.length > 0) return genreMatches;

  const regionAlias = key.includes("韩") ? "韩国" : key.includes("日") ? "日本" : "";
  const typeAlias = key.includes("剧") ? "tv" : key.includes("电影") ? "movie" : key.includes("综艺") ? "variety" : "";
  if (!regionAlias && !typeAlias) return direct;

  return items.filter((item) => {
    if (regionAlias && item.region !== regionAlias) return false;
    if (typeAlias && item.type !== typeAlias) return false;
    return true;
  });
}

export function sortMedia(items: MediaItem[], sort: FilterOptions["sort"] = "heat"): MediaItem[] {
  const list = [...items];
  if (sort === "rating") {
    return list.sort((a, b) => b.rating - a.rating || b.heat - a.heat);
  }
  if (sort === "latest") {
    return list.sort((a, b) => b.year - a.year || b.heat - a.heat);
  }
  if (sort === "update") {
    return list.sort((a, b) => b.year - a.year || b.heat - a.heat);
  }
  return list.sort((a, b) => b.heat - a.heat || b.rating - a.rating);
}

export function filterMedia(items: MediaItem[], options: FilterOptions): MediaItem[] {
  const { type, genres = [], region = "全部", year = "全部", keyword = "", sort = "heat" } = options;
  let list = type ? items.filter((item) => item.type === type) : [...items];

  if (keyword.trim()) {
    list = searchMedia(list, keyword);
  }
  if (genres.length > 0) {
    list = list.filter((item) => item.genres.some((genre) => genres.includes(genre)));
  }
  if (region && region !== "全部") {
    list = list.filter((item) => item.region === region);
  }
  if (year && year !== "全部") {
    list = list.filter((item) => String(item.year) === year);
  }
  return sortMedia(list, sort);
}

export function getRecommendations(items: MediaItem[], current: MediaItem, limit = 4): MediaItem[] {
  return items
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => current.genres.includes(genre)).length;
      const regionScore = item.region === current.region ? 3 : 0;
      const typeScore = item.type === current.type ? 4 : 0;
      return { item, score: sharedGenres * 10 + regionScore + typeScore + item.rating };
    })
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getRelatedNews(all: { id: string; relatedIds: string[] }[], currentId: string, relatedIds: string[], limit = 3) {
  const direct = all.filter((item) => relatedIds.includes(item.id) && item.id !== currentId);
  if (direct.length >= limit) return direct.slice(0, limit);
  const fallback = all.filter((item) => item.id !== currentId && !relatedIds.includes(item.id));
  return [...direct, ...fallback].slice(0, limit);
}